import React from 'react';
import { Home, Building, Calendar, DollarSign, Percent, PieChart } from 'lucide-react';
import { motion } from 'framer-motion';
export interface LoanDetails {
  id: string;
  propertyAddress: string;
  propertyType: 'Primary Residence' | 'Investment Property';
  loanType: string;
  originalAmount: number;
  currentBalance: number;
  interestRate: number;
  monthlyPayment: number;
  originationDate: string;
  estimatedHomeValue: number;
  ltv: number;
}
interface LoanCardProps {
  loan: LoanDetails;
}
export function LoanCard({
  loan
}: LoanCardProps) {
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
  const isPrimary = loan.propertyType === 'Primary Residence';
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-45">
      <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start" data-id="element-46">
        <div data-id="element-47">
          <div className="flex items-center gap-2 mb-1" data-id="element-48">
            {isPrimary ? <Home className="w-4 h-4 text-brand" data-id="element-49" /> : <Building className="w-4 h-4 text-brand" data-id="element-50" />}
            <span className="text-sm font-semibold text-brand uppercase tracking-wider" data-id="element-51">
              {loan.propertyType}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-1" data-id="element-52">
            {loan.propertyAddress}
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-53">{loan.loanType}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${isPrimary ? 'bg-brand/10 text-brand border-brand/20' : 'bg-brand/5 text-brand-muted border-brand/10'}`} data-id="element-54">
          Active
        </div>
      </div>

      <div className="p-5" data-id="element-55">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4" data-id="element-56">
          <div data-id="element-57">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-58">
              <DollarSign className="w-4 h-4" data-id="element-59" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-60">
                Current Balance
              </span>
            </div>
            <p className="text-xl font-bold text-brand" data-id="element-61">
              {formatCurrency(loan.currentBalance)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5" data-id="element-62">
              Original: {formatCurrency(loan.originalAmount)}
            </p>
          </div>

          <div data-id="element-63">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-64">
              <Percent className="w-4 h-4" data-id="element-65" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-66">
                Interest Rate
              </span>
            </div>
            <p className="text-xl font-bold text-brand" data-id="element-67">
              {loan.interestRate.toFixed(3)}%
            </p>
          </div>

          <div data-id="element-68">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-69">
              <Calendar className="w-4 h-4" data-id="element-70" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-71">
                Monthly (P&I)
              </span>
            </div>
            <p className="text-xl font-bold text-brand" data-id="element-72">
              {formatCurrency(loan.monthlyPayment)}
            </p>
          </div>

          <div data-id="element-73">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-74">
              <Home className="w-4 h-4" data-id="element-75" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-76">
                Est. Value
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-800" data-id="element-77">
              {formatCurrency(loan.estimatedHomeValue)}
            </p>
          </div>

          <div data-id="element-78">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-79">
              <PieChart className="w-4 h-4" data-id="element-80" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-81">
                Est. LTV
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-800" data-id="element-82">
              {loan.ltv.toFixed(1)}%
            </p>
          </div>

          <div data-id="element-83">
            <div className="flex items-center gap-1.5 text-gray-500 mb-1" data-id="element-84">
              <Calendar className="w-4 h-4" data-id="element-85" />
              <span className="text-xs font-medium uppercase tracking-wider" data-id="element-86">
                Originated
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-800" data-id="element-87">
              {loan.originationDate}
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}